import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { CallMedia, SOCKET_EVENTS, type Call } from '@forever/shared';
import { connectSocket } from '@/lib/socket';
import { useAuth } from '@/lib/auth';
import { CallOverlay } from './CallOverlay';
import { useBrowserCall } from './useBrowserCall';
import './call.css';

/**
 * Görüşmeyi uygulamanın tepesine bağlar.
 *
 * Gelen arama hangi sayfada olursa olsun yakalanmalı: diyetisyen ölçüm
 * girerken de aranabilir. Bu yüzden dinleyici tek tek sayfalarda değil, kökte
 * duruyor ve katman her şeyin üstüne biniyor.
 *
 * Herhangi bir sayfa `useCalls().startCall(...)` ile arama başlatabilir.
 */

interface CallsContextValue {
  /** Sohbet üzerinden arama başlatır. */
  startCall: (conversationId: string, peerName: string, media: CallMedia) => void;
  /** Görüşme sürüyorsa yeni arama açılmamalı — düğmeler buna bakar. */
  busy: boolean;
}

const CallsContext = createContext<CallsContextValue | null>(null);

export function useCalls(): CallsContextValue {
  const ctx = useContext(CallsContext);
  if (!ctx) throw new Error('useCalls yalnızca CallProvider içinde kullanılabilir.');
  return ctx;
}

export function CallProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const call = useBrowserCall();
  const [incoming, setIncoming] = useState<Call | null>(null);

  const { state, start, accept, decline, hangUp, toggleMic, toggleCamera, reset } = call;

  /* Gelen arama dinleyicisi. Oturum yoksa socket'e bağlanmıyoruz. */
  useEffect(() => {
    if (!user) return undefined;
    const socket = connectSocket();

    const onIncoming = (raw: unknown) => {
      const payload = raw as Call;
      /* Zaten bir görüşmedeysek yeni arama ekranı açılmamalı; sunucu
         tarafı da bunu engelliyor ama iki uç arasında yarış olabilir. */
      if (state.phase !== 'idle' && state.phase !== 'ended' && state.phase !== 'failed') return;
      setIncoming(payload);
    };

    socket.on(SOCKET_EVENTS.CALL_INCOMING, onIncoming);
    return () => {
      socket.off(SOCKET_EVENTS.CALL_INCOMING, onIncoming);
    };
  }, [user, state.phase]);

  /* Arama başka bir yerden (mobilden, zaman aşımından) kapandıysa gelen
     arama ekranı da kapanmalı. */
  useEffect(() => {
    if (state.phase === 'ended' || state.phase === 'failed') setIncoming(null);
  }, [state.phase]);

  const startCall = useCallback(
    (conversationId: string, peerName: string, media: CallMedia) => {
      void start(conversationId, media, peerName);
    },
    [start],
  );

  const handleAccept = useCallback(() => {
    if (!incoming) return;
    void accept(incoming);
  }, [accept, incoming]);

  const handleDecline = useCallback(() => {
    if (!incoming) return;
    decline(incoming.id);
    setIncoming(null);
  }, [decline, incoming]);

  const handleClose = useCallback(() => {
    setIncoming(null);
    reset();
  }, [reset]);

  const busy = state.phase !== 'idle' && state.phase !== 'ended' && state.phase !== 'failed';

  const value = useMemo<CallsContextValue>(() => ({ startCall, busy }), [startCall, busy]);

  /* Gelen arama ekranı yalnızca henüz cevaplanmadıysa gösterilir; cevaplayınca
     akış normal görüşme durumuna geçiyor. */
  const showingIncoming =
    incoming !== null && (state.phase === 'idle' || state.phase === 'requesting-permission');

  /* Gelen arama beklerken kancanın fazı hâlâ 'idle'; katman o fazda hiçbir şey
     çizmiyor. Burada 'ringing'e çeviriyoruz — kancanın kendi durumunu
     kirletmeden yalnızca görüntüleme için. */
  const overlayState = showingIncoming
    ? {
        ...state,
        phase: state.phase === 'idle' ? ('ringing' as const) : state.phase,
        media: incoming.media,
        peerName: incoming.initiatorName,
      }
    : state;

  return (
    <CallsContext.Provider value={value}>
      {children}
      {(state.phase !== 'idle' || showingIncoming) && (
        <CallOverlay
          state={overlayState}
          incoming={showingIncoming}
          localVideoRef={call.localVideoRef}
          remoteVideoRef={call.remoteVideoRef}
          remoteAudioRef={call.remoteAudioRef}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onHangUp={() => hangUp()}
          onToggleMic={toggleMic}
          onToggleCamera={toggleCamera}
          onClose={handleClose}
        />
      )}
    </CallsContext.Provider>
  );
}
